// frontend/src/auth/cognito-idp-mfa.ts
// Cognito MFA 登録系 API（TOTP / WebAuthn）を fetch で直接呼ぶ最小クライアント。
import type { Credential } from "../types/webauthn";

export interface AssociateSoftwareTokenResult {
  readonly SecretCode?: string;
  readonly Session?: string;
}

export interface VerifySoftwareTokenResult {
  readonly Status?: string;
  readonly Session?: string;
}

export interface StartWebAuthnRegistrationResult {
  readonly CredentialCreationOptions?: unknown;
}

export type AssociateSoftwareTokenInput =
  | { readonly accessToken: string; readonly session?: never }
  | { readonly accessToken?: never; readonly session: string };

export type VerifySoftwareTokenInput =
  | { readonly accessToken: string; readonly session?: never; readonly userCode: string }
  | { readonly accessToken?: never; readonly session: string; readonly userCode: string };

export interface CognitoMfa {
  associateSoftwareToken(input: AssociateSoftwareTokenInput): Promise<AssociateSoftwareTokenResult>;
  verifySoftwareToken(input: VerifySoftwareTokenInput): Promise<VerifySoftwareTokenResult>;
  setUserMfaPreference(input: { accessToken: string }): Promise<unknown>;
  startWebAuthnRegistration(input: {
    accessToken: string;
  }): Promise<StartWebAuthnRegistrationResult>;
  completeWebAuthnRegistration(input: {
    accessToken: string;
    credential: Credential;
  }): Promise<unknown>;
}

function endpoint(region: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/`;
}

async function call<T>(region: string, target: string, body: unknown): Promise<T> {
  const res = await fetch(endpoint(region), {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const type = typeof json["__type"] === "string" ? (json["__type"] as string) : "CognitoError";
    throw new Error(type);
  }
  return json as T;
}

export function createCognitoMfa(region: string): CognitoMfa {
  return {
    associateSoftwareToken: (input) =>
      call<AssociateSoftwareTokenResult>(
        region,
        "AssociateSoftwareToken",
        input.accessToken !== undefined
          ? { AccessToken: input.accessToken }
          : { Session: input.session },
      ),
    verifySoftwareToken: (input) =>
      call<VerifySoftwareTokenResult>(
        region,
        "VerifySoftwareToken",
        input.accessToken !== undefined
          ? { AccessToken: input.accessToken, UserCode: input.userCode, FriendlyDeviceName: "cloud-checker" }
          : { Session: input.session, UserCode: input.userCode, FriendlyDeviceName: "cloud-checker" },
      ),
    setUserMfaPreference: (input) =>
      call<unknown>(region, "SetUserMFAPreference", {
        AccessToken: input.accessToken,
        SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true },
      }),
    startWebAuthnRegistration: (input) =>
      call<StartWebAuthnRegistrationResult>(region, "StartWebAuthnRegistration", {
        AccessToken: input.accessToken,
      }),
    completeWebAuthnRegistration: (input) =>
      call<unknown>(region, "CompleteWebAuthnRegistration", {
        AccessToken: input.accessToken,
        Credential: input.credential,
      }),
  };
}
